import { Component, computed, ElementRef, inject, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgApexchartsModule } from 'ng-apexcharts';
import { ChartOptions } from './admin-statistics.model';
import { ClickOutsideDirective } from '../pharmacies/admin-pharmacies-list/filter/utils/clickOutside';

@Component({
  selector: 'app-admin-statistics',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule, FormsModule, ClickOutsideDirective],
  templateUrl: './admin-statistics.html',
  styleUrl: './admin-statistics.scss',
})
export class AdminStatistics {
  private locale = inject(LOCALE_ID);
  private host = inject(ElementRef<HTMLElement>);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  isArabic = computed(() => this.locale.startsWith('ar'));
  // $accentBlue from src/variables, exposed as --color-accent-blue on :host (see .scss)
  private chartColor = this.paletteColor('--color-accent-blue');
  stats = [
    {
      labelEn: 'Orders',
      labelAr: 'الطلبات',
      value: '2,345',
      change: '12.5%',
      increasing: true,
    },
    {
      labelEn: 'New Users',
      labelAr: 'المستخدمون الجدد',
      value: '1,230',
      change: '8.4%',
      increasing: false,
    },
    {
      labelEn: 'Pharmacies',
      labelAr: 'الصيدليات',
      value: '1,560',
      change: '24.1%',
      increasing: true,
    },
    {
      labelEn: 'Total Earnings',
      labelAr: 'إجمالي الأرباح',
      value: '84.500',
      change: '18.2%',
      increasing: true,
    },
  ];

  years = ['2022', '2023', '2024'];
  selectedYear = '2024';

  public ordersChartOptions: ChartOptions;
  public earningsChartOptions: ChartOptions;

  constructor() {
    this.ordersChartOptions = {
      series: [
        {
          name: 'Orders',
          data: [82, 45, 70, 65, 30, 55, 92, 40, 85, 50, 75, 60],
        },
      ],
      chart: {
        type: 'bar',
        height: 250,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '30%',
          borderRadius: 4,
        },
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        axisTicks: {
          show: false,
        },
      },
      grid: {
        show: true,
        borderColor: '#E6E6E6',
        strokeDashArray: 0,
        yaxis: {
          lines: {
            show: true,
          },
        },
        xaxis: {
          lines: {
            show: false,
          },
        },
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 15,
        },
      },
      colors: [this.chartColor],
      states: {
        hover: {
          filter: {
            type: 'darken',
          },
        },
        active: {
          filter: {
            type: 'darken',
          },
        },
      },
      tooltip: {
        theme: 'dark',
        y: {
          formatter: function (val) {
            return val.toString();
          },
        },
        marker: {
          show: false,
        },
      },
    };

    this.earningsChartOptions = {
      ...this.ordersChartOptions,
      series: [
        {
          name: 'Earnings',
          data: [55, 42, 48, 30, 65, 50, 75, 25, 80, 45, 60, 70],
        },
      ],
      tooltip: {
        theme: 'dark',
        y: {
          formatter: function (val) {
            return val + 'K';
          },
        },
        marker: {
          show: false,
        },
      },
    };
  }

  // Resolved to the actual value so exported chart images keep the colour; var() covers SSR.
  private paletteColor(name: string): string {
    const value = this.isBrowser
      ? getComputedStyle(this.host.nativeElement).getPropertyValue(name).trim()
      : '';
    return value || `var(${name})`;
  }

  onYearChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const year = selectElement.value;
    this.selectedYear = year;
    this.updateCharts();
  }

  updateCharts() {
    const randomOrders = Array.from({ length: 12 }, () => Math.floor(Math.random() * 25) + 27);
    const randomEarnings = Array.from({ length: 12 }, () => Math.floor(Math.random() * 25) + 27);
    this.ordersChartOptions = {
      ...this.ordersChartOptions,
      series: [
        {
          name: 'Orders',
          data: randomOrders,
        },
      ],
    };

    this.earningsChartOptions = {
      ...this.earningsChartOptions,
      series: [
        {
          name: 'Earnings',
          data: randomEarnings,
        },
      ],
    };
  }
}
